export type TicketNoteEntry = {
  id: string;
  note: string;
  createdOn: string;
  createdBy: string;
};

const NOTE_LIST_KEYS = [
  "ticket_notes",
  "notes",
  "results",
  "data",
  "items",
  "records",
  "rows",
  "list",
];

function extractNoteArray(raw: unknown): Record<string, unknown>[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter(
      (item): item is Record<string, unknown> =>
        Boolean(item) && typeof item === "object" && !Array.isArray(item)
    );
  }
  if (typeof raw !== "object") return [];
  const root = raw as Record<string, unknown>;
  for (const key of NOTE_LIST_KEYS) {
    const value = root[key];
    if (Array.isArray(value)) {
      return value.filter(
        (item): item is Record<string, unknown> =>
          Boolean(item) && typeof item === "object" && !Array.isArray(item)
      );
    }
  }
  return [];
}

function mapNoteRow(row: Record<string, unknown>, index: number): TicketNoteEntry {
  return {
    id: String(row.id ?? row.note_id ?? row.pk ?? `note-${index}`),
    note: String(row.note ?? row.content ?? row.body ?? row.text ?? "").trim(),
    createdOn: String(
      row.time ??
        row.created_on ??
        row.created_at ??
        row.createdAt ??
        row.timestamp ??
        ""
    ).trim(),
    createdBy: String(
      row.by ??
        row.created_by_name ??
        row.author_name ??
        row.author ??
        row.by_id ??
        row.created_by ??
        row.user_name ??
        row.user ??
        ""
    ).trim(),
  };
}

export function parseTicketNotesResponse(raw: unknown): TicketNoteEntry[] {
  const rows = extractNoteArray(raw);
  return rows
    .map(mapNoteRow)
    .filter(n => n.note.length > 0)
    .sort(
      (a, b) =>
        new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
    );
}

function noteToRawRow(note: TicketNoteEntry): Record<string, unknown> {
  return {
    id: note.id,
    text: note.note,
    time: note.createdOn || new Date().toISOString(),
    by: note.createdBy,
    // Legacy shapes for older cache payloads
    note: note.note,
    created_on: note.createdOn || new Date().toISOString(),
    created_by_name: note.createdBy,
  };
}

/** Parse POST /ticket_notes/ response into a single note entry. */
export function parseCreatedTicketNote(raw: unknown): TicketNoteEntry | null {
  if (!raw) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const root = raw as Record<string, unknown>;
    const text = String(
      root.note ?? root.content ?? root.body ?? root.text ?? ""
    ).trim();
    if (text) {
      return mapNoteRow(root, 0);
    }
    for (const key of ["data", "ticket_note", "result", "record"]) {
      const nested = root[key];
      if (nested && typeof nested === "object" && !Array.isArray(nested)) {
        const parsed = mapNoteRow(nested as Record<string, unknown>, 0);
        if (parsed.note) return parsed;
      }
    }
  }
  const list = parseTicketNotesResponse(raw);
  return list.length > 0 ? list[list.length - 1] : null;
}

/** Append a saved note to React Query cache payload (list GET shapes). */
export function appendTicketNoteToCache(
  existingRaw: unknown,
  note: TicketNoteEntry
): unknown {
  const existing = parseTicketNotesResponse(existingRaw);
  const duplicate = existing.some(
    n =>
      n.id === note.id ||
      (n.note === note.note &&
        Math.abs(
          new Date(n.createdOn || 0).getTime() -
            new Date(note.createdOn || 0).getTime()
        ) < 10_000)
  );
  if (duplicate) return existingRaw ?? existing;

  const row = noteToRawRow(note);
  if (Array.isArray(existingRaw)) {
    return [...existingRaw, row];
  }
  if (existingRaw && typeof existingRaw === "object") {
    const root = existingRaw as Record<string, unknown>;
    for (const key of NOTE_LIST_KEYS) {
      if (Array.isArray(root[key])) {
        return { ...root, [key]: [...(root[key] as unknown[]), row] };
      }
    }
    return {
      ...root,
      ticket_notes: [...existing.map(noteToRawRow), row],
    };
  }
  return [row];
}

export function isTicketNotesNotFoundError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 404;
}
